<template>
  <Modal
    v-if="allOrganisations"
    class="organisation-group-removal-dialog text-left"
    v-model="shown"
    title="Create new organisation"
  >
    <div style="width: 100%">
      <p class="text-big" id="org-name-label">What should the name of the new Organisation be?</p>
      <p><span class="text-warning">Note:</span> it must be unique across the Gyre platform.</p>
      <div class="row">
        <div class="col-sm-12 text-big" :class="{ 'has-error': v$.name.$error }">
          <input
            type="text"
            class="form-control"
            v-model="name"
            @blur="v$.name.$touch()"
            maxlength="40"
            aria-labelledby="org-name-label"
          />
        </div>
      </div>
      <p class="text-danger text-left" v-for="error in errors" :key="error">{{ error }}</p>
      <!-- Display validation errors -->
      <p class="text-danger text-left" v-if="v$.name.$error">
        <span v-if="v$.name.required.$invalid">Name is required.</span>
        <span v-if="v$.name.maxLength.$invalid">Name must be at most 40 characters.</span>
        <span v-if="v$.name.regex.$invalid">Name does not match the required format.</span>
      </p>
    </div>

    <template #footer>
      <div>
        <button
          class="btn btn-primary btn-lg"
          @click="createOrganisation()"
          :disabled="!name || v$.name.$invalid"
          type="button"
        >
          Create
        </button>

        <button class="btn btn-default btn-lg" @click="cancel()" type="button">Cancel</button>
      </div>
    </template>
  </Modal>
</template>
<script lang="ts" setup>
import { computed, reactive, ref } from "vue";
import axios from "axios";
import { Modal } from "uiv";
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength, helpers } from "@vuelidate/validators";
import organisationNameRegex from "../../../services/utilities/organisation-name-regex";
import {
  OrganisationAndMembersApiModel,
  OrganisationDescriptionApiModel,
} from "../../../api-models";

const resolveCallback = ref<((value: OrganisationAndMembersApiModel | null) => void) | null>(null);
const rejectCallback = ref<((reason?: any) => void) | null>(null);
const errors = ref<string[]>([]);
const allOrganisations = ref<OrganisationDescriptionApiModel[] | null>(null);
const name = ref("");
const shown = ref(false);

const orgNameRegex = helpers.regex(organisationNameRegex);

const rules = computed(() => ({
  name: {
    required,
    maxLength: maxLength(40),
    regex: (value: string) => orgNameRegex(value),
  },
}));

const state = reactive({ name });

const v$ = useVuelidate(rules, state, { $autoDirty: true });

const close = () => {
  v$.value.$reset();
  shown.value = false;
};

const cancel = () => {
  close();
  if (resolveCallback.value) {
    resolveCallback.value(null);
  }
};

const open = (
  allOrganisationsParam: OrganisationDescriptionApiModel[],
): Promise<OrganisationAndMembersApiModel | null> => {
  allOrganisations.value = allOrganisationsParam;
  name.value = "";
  errors.value = [];
  shown.value = true;
  return new Promise<OrganisationAndMembersApiModel | null>((resolve, reject) => {
    resolveCallback.value = resolve;
    rejectCallback.value = reject;
  });
};

const createOrganisation = () => {
  v$.value.$touch();
  const isValid = !v$.value.$invalid;

  if (!isValid) {
    return;
  }

  axios
    .post("/api/organisation/", JSON.stringify(name.value), {
      headers: { "content-type": "application/json" },
      validateStatus(status) {
        return status === 200 || status === 409; // Conflict
      },
    })
    .then((res) => {
      if (res.status === 200) {
        errors.value = [];
        close();
        const newOrganisationModel = res.data as OrganisationAndMembersApiModel;
        if (resolveCallback.value) {
          resolveCallback.value(newOrganisationModel);
        }
      } else {
        errors.value = ["That name is already taken, please choose another one."];
      }
    })
    .catch(() => {
      errors.value = ["There was a problem creating the organisation, please try again."];
    });
};

defineExpose({ open, close });
</script>
